import { io } from "socket.io-client";
import { showTodoConfirmationAlert } from "../core/common/useAlert";
import axios from "axios";
import { all_routes } from "../feature-module/router/all_routes";

const routes = all_routes;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL_SOCKET_NOTIFICATION, // adjust if needed 
});

// Request Interceptor: Adds Authorization token if available in localStorage
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token"); // Get the token from localStorage
    if (token) {
      config.headers["authorization"] = `${token}`; // Attach the token
    }
    // If the data is FormData, remove the Content-Type header
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
      config.headers["Content-Type"] = "multipart/form-data";
    } else {
      // Set Content-Type to application/json for regular JSON data
      config.headers["Content-Type"] = "application/json";
    }
    return config; // Proceed with the request
  },
  (error) => {
    return Promise.reject(error); // Reject the request if there is an error
  }
);

class SocketService {
  constructor() {
    this.socket = null;
    this.notifications = [];
    this.unreadCount = 0;
    this.updateCallbacks = [];
  }

  connect() {
    this.socket = io(process.env.REACT_APP_API_BASE_URL_SOCKET_NOTIFICATION, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      auth: {
        token: localStorage.getItem("token"),
      },
    });

    this.socket.on("connect", () => { });

    this.socket.on("disconnect", () => { });

    this.socket.on("connect_error", (err) => {
      console.error("Socket connection error:", err);
    });
  }

  initialize(userId) {
    this.connect();
    this.joinUserRoom(userId);
    this.setupNotificationListener();
    this.fetchInitialNotifications(userId);
  }

  joinUserRoom(userId) {
    if (this.socket) {
      this.socket.emit("subscribe", userId);
    }
  }

  onUpdate(callback) {
    this.updateCallbacks.push(callback);
    return () => {
      this.updateCallbacks = this.updateCallbacks.filter(
        (cb) => cb !== callback
      );
    };
  }

  notifyUpdate() {
    this.updateCallbacks.forEach((cb) =>
      cb({
        notifications: this.notifications,
        unreadCount: this.unreadCount,
      })
    );
  }

  async fetchInitialNotifications(userId) {
    try {
      // Make API call to fetch unread notifications
      const signature = localStorage.getItem("signature");
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post("/getUnreadNotifications", {
        signature,
      });

      if (response.data && response.data.data) {
        const notificationPayload = response?.data?.data?.data || [];

        this.notifications = notificationPayload.map((notification) => ({
          ...notification,
          id: notification.id || Date.now(),
          is_read: false,
          createdAt: notification.createdAt || new Date().toISOString(),
        }));

        this.unreadCount = this.notifications.length;
        this.notifyUpdate();
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Handle specific error cases
      if (error.response) {
        // The request was made and the server responded with a status code
        console.error("Server responded with:", error.response.status);
        if (error.response.status === 401) {
          // Handle unauthorized (token might be expired)
          console.error("Authentication failed, token might be expired");
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error("No response received from server");
      } else {
        // Something happened in setting up the request
        console.error("Error setting up request:", error.message);
      }

      // Initialize with empty array if there's an error
      this.notifications = [];
      this.unreadCount = 0;
      this.notifyUpdate();
    }
  }

  setupNotificationListener() {
    if (this.socket) {
      this.socket.on("new_todo_notification", (data) => {
        // Add to notifications array
        const newNotification = {
          ...data,
          id: Date.now(), // temporary ID
          createdAt: new Date().toISOString(),
          is_read: false,
        };

        this.notifications.unshift(newNotification);
        this.unreadCount++;
        this.notifyUpdate();

        this.showTodoNotification(data);
      });

      // / Individual Todo Notifications
      this.socket.on("todo_reminder_notification", (data) => {
        const newNotification = {
          ...data,
          id: Date.now(), // temp ID
          createdAt: new Date().toISOString(),
          is_read: false,
        };

        this.notifications.unshift(newNotification);
        this.unreadCount++;
        this.notifyUpdate();
        this.showTodoNotification(data);
      });

      // Team Todo Overdue Notifications
      this.socket.on("team_todo_overdue_notification", (data) => {
        const newNotification = {
          ...data,
          id: Date.now(),
          createdAt: new Date().toISOString(),
          is_read: false,
        };

        this.notifications.unshift(newNotification);
        this.unreadCount++;
        this.notifyUpdate();
        this.showTodoNotification(data);
      });
    }
  }

  async markAsRead(notificationId) {
    try {
      const signature = localStorage.getItem("signature");
      const response = await axiosInstance.post(`/markRead`, {
        id: notificationId,
        signature,
      });

      if (response.data.message[0].status) {
        const notification = this.notifications.find(
          (n) => n.id === notificationId
        );
        if (notification && !notification.is_read) {
          notification.is_read = true;
          this.unreadCount--;
          this.notifyUpdate();
        }
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  }

  async markAllAsRead() {
    try {
      const signature = localStorage.getItem("signature");
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL_SOCKET_NOTIFICATION}/markAllRead`,
        { signature }
      );

      if (response.data.message[0].status) {
        this.notifications.forEach((n) => {
          if (!n.is_read) {
            n.is_read = true;
          }
        });
        this.unreadCount = 0;
        this.notifyUpdate();
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  }

  showTodoNotification(notificationData) {
    const currentUserId = localStorage.getItem("userId");
    if (notificationData.user_id == currentUserId) {
      const lastWord = notificationData.message;
      let capitalizedText =
        lastWord.charAt(0).toUpperCase() + lastWord.slice(1);

      showTodoConfirmationAlert(
        notificationData.title,
        capitalizedText,
        notificationData.created_by.name,
        () => {
          // if (notificationData.todo_id) {
          //   window.location.href = `/application/todo/`;
          // }
        },
        routes.TodoList,
        notificationData.created_by.image,
      );
    }
  }

  // Don't remove these comments below this is for auto hide notification pop-up
  // showTodoNotification(notificationData) {
  //   const currentUserId = localStorage.getItem("userId");
  //   if (notificationData.user_id == currentUserId) {
  //     const lastWord = notificationData.message.trim().split(" ").pop();
  //     notification.open({
  //       message: notificationData.title || "New Notification",
  //       description: lastWord,
  //       duration: 5,
  //       onClick: () => {
  //         // if (notificationData.todo_id) {
  //         //   window.location.href = `/application/todo/`;
  //         // }
  //       },
  //     });
  //   }
  // }

  disconnect() {
    if (this.socket) {
      this.socket.off("new_todo_notification");
      this.socket.disconnect();
      this.socket = null;
    }
  }
}

export default new SocketService();
